/**
 * Phase A: Step-based submission helpers (read-only)
 * 
 * Canonical StepKey: stepId ?? role ?? 'primary'
 * Labels read priority: labelsByStep[stepKey] → labelsByRole[role] → labels[]
 */

type MediaRole = 'primary' | 'expiration_label' | 'after';

interface MediaItem {
  role: MediaRole;
  stepId?: string;
  [key: string]: any;
}

export interface LabelData {
  categoryId: string; 
  bbox: [number, number, number, number]; 
  attributes?: any;
  flagged?: boolean;
  flagReason?: string;
  flagType?: 'redo' | 'wrong_annotation' | 'annotation_not_needed' | 'other';
  attributeFlags?: Record<string, {
    flagType: string;
    flagReason: string;
    flaggedAt?: number;
    flaggedBy?: string;
  }>;
}

/**
 * Get canonical stepKey from a media item
 * @param mediaItem Media item with role and optional stepId
 * @returns stepId ?? role ?? 'primary'
 */
export function stepKeyFromMedia(mediaItem: MediaItem | null | undefined): string {
  if (!mediaItem) return 'primary';
  return mediaItem.stepId ?? mediaItem.role ?? 'primary';
}

/**
 * Get all unique stepKeys from submission.media array
 * Returns ordered list of stepKeys (stepId ?? role)
 * @param submission Submission object
 * @returns Array of unique stepKeys in order
 */
export function getSubmissionStepKeys(submission: any): string[] {
  if (!submission?.media || !Array.isArray(submission.media)) {
    // Legacy single-photo: return ['primary']
    return ['primary'];
  }
  
  const stepKeys = submission.media
    .map((m: MediaItem) => stepKeyFromMedia(m))
    .filter((key: string, index: number, arr: string[]) => arr.indexOf(key) === index); // unique
  
  return stepKeys.length > 0 ? stepKeys : ['primary'];
}

/**
 * Get labels for a specific stepKey with fallback priority:
 * 1. labelsByStep[stepKey]
 * 2. labelsByRole[role] (if stepKey came from role)
 * 3. labels[] (legacy single-photo)
 * 
 * @param submission Submission object
 * @param stepKey StepKey to get labels for
 * @returns Array of labels or empty array
 */
export function getLabelsForStep(submission: any, stepKey: string): LabelData[] {
  if (!submission) return [];
  
  // Priority 1: labelsByStep[stepKey]
  if (submission.labelsByStep && typeof submission.labelsByStep === 'object') {
    const stepLabels = submission.labelsByStep[stepKey];
    if (Array.isArray(stepLabels) && stepLabels.length > 0) {
      return stepLabels;
    }
  }
  
  // Priority 2: labelsByRole[role] - try to find role from submission.media first
  // (No whitelist - accepts any stepKey)
  if (submission.media && Array.isArray(submission.media)) {
    const mediaItem = submission.media.find((m: MediaItem) => 
      stepKeyFromMedia(m) === stepKey
    );
    if (mediaItem && mediaItem.role) {
      const roleLabels = submission.labelsByRole?.[mediaItem.role];
      if (Array.isArray(roleLabels) && roleLabels.length > 0) {
        return roleLabels;
      }
    }
  }
  
  // Priority 3: Fallback - if stepKey is a known role, try labelsByRole[stepKey] directly
  // (This handles legacy cases where stepKey equals role)
  if (submission.labelsByRole && typeof submission.labelsByRole === 'object') {
    const roleLabels = submission.labelsByRole[stepKey];
    if (Array.isArray(roleLabels) && roleLabels.length > 0) {
      return roleLabels;
    }
  }
  
  // Priority 4: Legacy labels[] (single-photo)
  if (Array.isArray(submission.labels) && submission.labels.length > 0) {
    // Only return if this is likely a single-photo submission
    if (!submission.media || !Array.isArray(submission.media) || submission.media.length <= 1) {
      return submission.labels;
    }
  }
  
  return [];
}

/**
 * Get answers from submission
 * @param submission Submission object
 * @returns Answers object or empty object
 */
export function getAnswers(submission: any): Record<string, any> {
  if (!submission) return {};
  return submission.answers || {};
}

/**
 * Get required stepKeys for a submission
 * ALWAYS derives from submission.media[], NEVER from task.steps
 * This protects WIP submissions from task edits
 * 
 * @param submission Submission object
 * @returns Array of required stepKeys
 */
export function getRequiredSteps(submission: any): string[] {
  return getSubmissionStepKeys(submission);
}

/**
 * Get display label for a stepKey from task.steps or fallback to stepKey
 * @param stepKey Canonical stepKey
 * @param task Task object (may have task.steps[])
 * @returns Display label (step.title or stepKey)
 */
export function getStepDisplayLabel(stepKey: string, task?: any): string {
  if (!task?.steps || !Array.isArray(task.steps)) {
    return stepKey; // Fallback to raw stepKey
  }
  
  const step = task.steps.find((s: any) => s.stepId === stepKey);
  if (step?.title) {
    return step.title;
  }
  
  return stepKey; // Fallback to raw stepKey
}

/**
 * Map stepKey back to role (for legacy compatibility)
 * @param stepKey StepKey to map
 * @param submission Submission object (to check media items)
 * @returns MediaRole or null
 */
export function stepKeyToRole(stepKey: string, submission?: any): MediaRole | null {
  // If stepKey is a known role, return it
  if (['primary', 'expiration_label', 'after'].includes(stepKey)) {
    return stepKey as MediaRole;
  }
  
  // Try to find role from submission.media
  if (submission?.media && Array.isArray(submission.media)) {
    const mediaItem = submission.media.find((m: MediaItem) => 
      stepKeyFromMedia(m) === stepKey
    );
    if (mediaItem?.role) {
      return mediaItem.role;
    }
  }
  
  return null;
}


